import { useMemo } from "react";
import { SWRConfiguration } from "swr";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { useCriteria } from "../blocks/Accessories/Filters/Criteria/useCriteria";
import { useLanguage } from "../i18n/useLanguage";
import { DealerPackageEditorRoute } from "../navigation/DealerRoutes";
import { useRouteParams } from "../navigation/useRouteParams";
import { getPartName } from "../util/getPartName";
import { searchPredicate } from "../util/searchPredicate";
import { usePackageEditorSetting } from "../util/usePackageEditorSetting";
import { PackagePartItem } from "./useSearchPartPackagesByVehicle";

export const useGetPartPackagesByDealerId = (
  swrOptions: SWRConfiguration<FetcherResult<Payload>> = {}
): ModifiedSWRResponse<FetcherResult<Payload>, Payload> => {
  const [criteria] = useCriteria();
  const [language] = useLanguage();
  const route = useRouteParams(DealerPackageEditorRoute);
  const hasPackageEditor = usePackageEditorSetting();

  const { data, ...result } = useSwrGet<Payload>(
    route && hasPackageEditor
      ? "/retailSiteWebService/parts/partPackage/getPartPackagesByDealerId.php"
      : null,
    {
      dealerId: route?.dealerId || "",
    },
    swrOptions
  );

  const partPackages = useMemo(
    () =>
      data?.partPackages.filter((part) => {
        const text = [
          getPartName(part, language),
          part.partDetails.primaryPart.itemId,
          part.partDetails.primaryPart.packagePartNumber,
        ].join(" ");
        const matched = searchPredicate(text, criteria);
        return matched;
      }),
    [data, criteria]
  );

  if (!partPackages) {
    return { data, ...result };
  }

  return { ...result, data: { partPackages } };
};

type Payload = { partPackages: PackagePartItem[] };
